/**
 * overworldActor — a thin, scene-friendly wrapper around the Foundation
 * `SpriteResolver.overworldSprite()` result. It turns the two-shape `OverworldSprite`
 * union (an animated, SLICED PMD spritesheet vs a static battle-front fallback) into
 * one small handle a populated scene can drive without re-implementing the
 * sheet-vs-static branching at every call site (TownScene + RouteScene both need it
 * for residents, wild creatures, and the player's lead-Pokemon follower).
 *
 * It owns nothing but presentation: a Phaser sprite, its facing, and walk/idle
 * playback. Direction is the PMD `Direction` (8-way, row 0 = south). The renderer
 * stays rules-free — truth (which species, where) comes from the GameSession.
 *
 * Pure direction math lives in ./direction (Phaser-free, node-testable) and is
 * re-exported here for callers' convenience.
 */
import Phaser from 'phaser';
import type { Direction } from './paths.js';
import type { OverworldSprite } from './SpriteResolver.js';

export { directionFromVector } from './direction.js';

/** A live, directional overworld Pokemon sprite (PMD chibi, or a static fallback). */
export interface OverworldActor {
  /** The Phaser display object (origin bottom-centre: feet on its world position). */
  readonly sprite: Phaser.GameObjects.Sprite;
  /** True when this is a real SLICED PMD sheet (animations available). */
  readonly animated: boolean;
  /**
   * STABLE reference frame size (px) used for scaling — for a sliced PMD sheet this is
   * the sheet's NOMINAL per-frame width/height (`AnimData.xml`'s FrameWidth/Height),
   * which is identical across every idle + walk cell. Scaling against this (never the
   * currently-displayed `sprite.width/height`) keeps a follower's rendered footprint
   * CONSTANT through the walk cycle — PMD walk frames are taller than the standing
   * frame, so measuring the live frame would balloon a moving follower past its cap.
   */
  readonly frameWidth: number;
  readonly frameHeight: number;
  /** Face `dir` while standing still (plays the idle/standing pose for that facing). */
  face(dir: Direction): void;
  /** Walk facing `dir` (plays that direction's walk cycle; idempotent per facing). */
  walk(dir: Direction): void;
  /** Settle to a standing idle facing the last direction (idempotent). */
  idle(): void;
}

/**
 * The STABLE frame dimensions to scale an actor by — the nominal sliced-frame size for
 * a PMD sheet (constant across idle/walk), or the static fallback's source-image size
 * (a non-animated image has one constant size anyway). Pure; safe with degenerate dims.
 */
export function stableActorFrame(actor: OverworldActor): { width: number; height: number } {
  return {
    width: actor.frameWidth > 0 ? actor.frameWidth : 1,
    height: actor.frameHeight > 0 ? actor.frameHeight : 1,
  };
}

/**
 * Scale an actor so its tallest STABLE side fits `maxSide` (px), capped at `maxScale`.
 * Uses the nominal frame size, NOT the live (animation-frame-dependent) sprite size, so
 * the rendered footprint stays constant across idle + walk. The resulting tallest-side
 * footprint is `min(maxSide, maxScale * tallestStableSide)`.
 */
export function fitOverworldActor(
  actor: OverworldActor,
  maxSide: number,
  maxScale = Number.POSITIVE_INFINITY,
): void {
  const { width, height } = stableActorFrame(actor);
  const scale = Math.min(maxSide / width, maxSide / height, maxScale);
  actor.sprite.setScale(scale);
}

/**
 * Build an {@link OverworldActor} from a resolved {@link OverworldSprite}. For a
 * `sheet` result this creates a Phaser Sprite on the sliced walk sheet (south-facing
 * standing frame) and exposes per-direction walk/idle playback. For the `static`
 * fallback it creates a plain south-facing image-as-sprite with inert directional
 * methods (a wild/trainer with no PMD coverage still renders *something*).
 *
 * The sprite is added at (x, y) with origin (0.5, 1) so its feet sit on the position,
 * matching how the human avatar + PMD chibis are placed in the populated scenes.
 */
export function createOverworldActor(
  scene: Phaser.Scene,
  ow: OverworldSprite,
  x = 0,
  y = 0,
): OverworldActor {
  if (ow.kind === 'static') {
    const sprite = scene.add.sprite(x, y, ow.textureKey);
    sprite.setOrigin(0.5, 1);
    // A static fallback isn't animated, so the live frame size IS its stable size.
    const src = sprite.texture.getSourceImage() as { width?: number; height?: number };
    return {
      sprite,
      animated: false,
      frameWidth: src?.width || sprite.width || 1,
      frameHeight: src?.height || sprite.height || 1,
      // No PMD sheet ⇒ no real facings; flip horizontally as a gentle cue instead.
      face(dir: Direction): void {
        sprite.setFlipX(dir === 'west' || dir === 'north-west' || dir === 'south-west');
      },
      walk(dir: Direction): void {
        this.face(dir);
      },
      idle(): void {
        /* a static fallback has nothing to settle */
      },
    };
  }

  const sprite = scene.add.sprite(x, y, ow.walkTextureKey, ow.defaultFrame);
  sprite.setOrigin(0.5, 1);

  let facing: Direction = ow.defaultFacing;
  let mode: 'walk' | 'idle' = 'idle';

  const apply = (): void => {
    const key = mode === 'walk' ? ow.walkAnimKey(facing) : ow.idleAnimKey(facing);
    if (scene.anims.exists(key)) {
      sprite.play(key, true);
    } else {
      // Direction's row may be absent on a partial sheet — hold the standing cell.
      sprite.stop();
      sprite.setFrame(ow.standingFrame(facing));
    }
  };

  // Start settled, facing south.
  sprite.setFrame(ow.standingFrame(facing));

  return {
    sprite,
    animated: true,
    // The sheet's NOMINAL per-frame size — constant across idle + walk cells.
    frameWidth: ow.frameWidth > 0 ? ow.frameWidth : 1,
    frameHeight: ow.frameHeight > 0 ? ow.frameHeight : 1,
    face(dir: Direction): void {
      if (dir === facing && mode === 'idle') return;
      facing = dir;
      mode = 'idle';
      apply();
    },
    walk(dir: Direction): void {
      if (dir === facing && mode === 'walk') return;
      facing = dir;
      mode = 'walk';
      apply();
    },
    idle(): void {
      if (mode === 'idle') return;
      mode = 'idle';
      apply();
    },
  };
}
